/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { QueryResult } from "src/utils/results/types";
import { Session } from "src/utils/users/types";

export const resultStatusEmpty = (result?: QueryResult): boolean => {
  return result?.matrix?.rows.length === 0;
};

export const resultStatusDone = (result?: QueryResult): boolean => {
  return !resultStatusQueued(result) && !resultStatusRunning(result);
};

export const resultStatusQueued = (result?: QueryResult): boolean => {
  return typeof result?.job?.queue_position === "number";
};

export const resultStatusRunningTTL = (result?: QueryResult): boolean => {
  return result?.job?.category === "ttl";
};

export const resultStatusRunning = (result?: QueryResult): boolean => {
  return Boolean(result?.job?.is_running);
};

export const resultStatusOwned = (
  result?: QueryResult,
  session?: Session
): boolean => {
  return Boolean(session && session.user!.id === result?.job?.user_id);
};

export const resultStatusTextLong = (result?: QueryResult) => {
  return resultStatusQueued(result)
    ? `Currently queued.`
    : resultStatusRunning(result)
    ? "Currently running."
    : undefined;
};

// The different types of states fetching query results
// can be in.
type StatusType =
  | "loading"
  | "queued"
  | "queuedWithStaleResult"
  | "running"
  | "runningWithStaleResult"
  | "error"
  | "errorWithStaleResult"
  | "emptyResults"
  | "complete"
  | "unknown";

export const resultStatusType = (result?: QueryResult): StatusType => {
  if (result) {
    if (resultStatusQueued(result)) {
      return result.matrix ? "queuedWithStaleResult" : "queued";
    }
    if (resultStatusRunning(result)) {
      return result.matrix ? "runningWithStaleResult" : "running";
    }
    if (result.error) {
      return result.matrix ? "errorWithStaleResult" : "error";
    }
    if (result.fetchError) {
      return "error";
    }
    if (result.matrix?.rows) {
      return result.matrix?.rows?.length > 0 ? "complete" : "emptyResults";
    }
    if (result.loading) {
      return "loading";
    }
  }
  return "unknown";
};

export const resultStatusIcon = (result?: QueryResult) => {
  return resultStatusQueued(result)
    ? "clock"
    : resultStatusRunning(result)
    ? "running"
    : resultStatusError(result)
    ? "exclamation-circle"
    : "check";
};

export const resultStatusError = (result?: QueryResult) => {
  const message = result?.error?.message ?? result?.fetchError?.message;

  if (!message) {
    return;
  }

  return message.endsWith(".")
    ? `Error: ${capitalizeFirst(message)}`
    : `Error: ${capitalizeFirst(message)}.`;
};

const capitalizeFirst = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};
